import React, { useEffect, useState } from "react";
import { adminApi, resolveImage } from "../../lib/admin-api";
import { invalidateSettingsCache } from "../../hooks/useSettings";
import { Upload, RefreshCw, Save, Lock, Shuffle, RotateCcw, Image as ImageIcon } from "lucide-react";

export default function AdminSettings() {
  const [form, setForm] = useState(null);
  const [altUrl, setAltUrl] = useState("");
  const [logoUrl, setLogoUrl] = useState("/logo.png");
  const [logoBusy, setLogoBusy] = useState(false);
  const [whitelist, setWhitelist] = useState([]);
  const [saving, setSaving] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      const [s, w] = await Promise.all([adminApi.get("/settings"), adminApi.get("/whitelist")]);
      setForm({
        accent_color: s.data.accent_color || "#B5562E",
        manifesto_nl: s.data.manifesto_nl || "",
        manifesto_en: s.data.manifesto_en || "",
        hero_image_url: s.data.hero_image_url || "",
        about_us_nl: s.data.about_us_nl || "",
        about_us_en: s.data.about_us_en || "",
      });
      setAltUrl(s.data.hero_image_url_alt || "");
      setLogoUrl(s.data.logo_url || "/logo.png");
      setWhitelist(w.data.admin_emails || []);
    })();
  }, []);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setLogoBusy(true); setError("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await adminApi.post("/settings/upload-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoUrl(r.data.logo_url);
      window.dispatchEvent(new Event("destoel:settings-changed"));
    } catch (err) {
      setError(err?.response?.data?.detail || "Logo-upload mislukt");
    } finally {
      setLogoBusy(false);
    }
  };

  const resetLogo = async () => {
    setLogoBusy(true); setError("");
    try {
      const r = await adminApi.post("/settings/reset-logo");
      setLogoUrl(r.data.logo_url);
      window.dispatchEvent(new Event("destoel:settings-changed"));
    } catch (err) {
      setError(err?.response?.data?.detail || "Reset mislukt");
    } finally {
      setLogoBusy(false);
    }
  };

  const swapHero = async () => {
    setImgBusy(true); setError("");
    try {
      const r = await adminApi.post("/settings/swap-hero");
      set("hero_image_url", r.data.hero_image_url);
      setAltUrl(r.data.hero_image_url_alt || "");
    } catch (err) {
      setError(err?.response?.data?.detail || "Wisselen mislukt");
    } finally {
      setImgBusy(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImgBusy(true); setError("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await adminApi.post("/images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("hero_image_url", r.data.url);
    } catch (err) { setError(err?.response?.data?.detail || "Upload mislukt"); }
    finally { setImgBusy(false); }
  };

  const regen = async () => {
    setImgBusy(true); setError("");
    try {
      const r = await adminApi.post("/images/generate", {
        prompt_context: "warm sunlit modernist living room with a single sculptural lounge chair, walnut floor, off-white walls, terracotta cushion",
        kind: "hero", out_slug: `hero_main_${Date.now()}`,
      });
      set("hero_image_url", r.data.url);
    } catch (err) { setError(err?.response?.data?.detail || "Beeld genereren mislukt"); }
    finally { setImgBusy(false); }
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      await adminApi.patch("/settings", form);
      setSavedAt(new Date());
      invalidateSettingsCache();
    } catch (err) { setError(err?.response?.data?.detail || "Opslaan mislukt"); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="text-stoel-inkSoft animate-pulse">Laden…</div>;

  return (
    <div data-testid="admin-settings" className="max-w-3xl">
      <div className="text-xs uppercase tracking-[0.3em] text-stoel-walnut mb-3">— Configuratie</div>
      <h1 className="stoel-display text-4xl mb-10">Settings</h1>

      <div className="space-y-8">
        <Section title="Site-logo">
          <div className="flex items-start gap-5">
            <div className="bg-stoel-paperDeep border border-stoel-line w-28 h-28 flex items-center justify-center p-3">
              {logoUrl
                ? <img data-testid="logo-preview" src={resolveImage(logoUrl) + `?v=${(savedAt || new Date()).getTime()}`} alt="Logo preview" className="w-full h-full object-contain" />
                : <ImageIcon size={36} className="text-stoel-walnut" />
              }
            </div>
            <div className="flex flex-col gap-2 flex-1">
              <label className="inline-flex items-center gap-2 px-3 py-2 border border-stoel-ink text-sm cursor-pointer hover:bg-stoel-ink hover:text-stoel-paper w-fit">
                <Upload size={14} /> Vervang logo
                <input
                  data-testid="logo-upload"
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  hidden
                  onChange={uploadLogo}
                  disabled={logoBusy}
                />
              </label>
              <button
                data-testid="logo-reset"
                onClick={resetLogo}
                disabled={logoBusy}
                className="inline-flex items-center gap-2 px-3 py-2 border border-stoel-line text-sm hover:bg-stoel-paperDeep disabled:opacity-50 w-fit"
              >
                <RotateCcw size={14} /> {logoBusy ? "Bezig…" : "Reset naar standaardlogo"}
              </button>
              <p className="text-xs text-stoel-inkSoft mt-1 max-w-md">
                PNG, JPG, SVG of WEBP — maximaal 2&nbsp;MB. Het logo verschijnt direct in de header en footer van de publieke site (zonder redeploy).
              </p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-stoel-walnut mt-1">
                Huidige bron: <span data-testid="logo-current-url" className="font-mono normal-case tracking-normal">{logoUrl}</span>
              </p>
            </div>
          </div>
        </Section>

        <Section title="Accent kleur">
          <div className="flex items-center gap-4">
            <input
              type="color"
              data-testid="accent-color-picker"
              value={form.accent_color}
              onChange={(e) => set("accent_color", e.target.value)}
              className="w-16 h-12 cursor-pointer border border-stoel-line"
            />
            <input data-testid="accent-color-input" value={form.accent_color} onChange={(e) => set("accent_color", e.target.value)} className={inputCls + " w-40 font-mono"} />
            <span className="text-xs text-stoel-inkSoft">Standaard: #B5562E (terracotta)</span>
          </div>
        </Section>

        <Section title="Manifest NL">
          <textarea data-testid="settings-manifesto-nl" rows={4} value={form.manifesto_nl} onChange={(e) => set("manifesto_nl", e.target.value)} className={inputCls} />
        </Section>

        <Section title="Manifest EN">
          <textarea data-testid="settings-manifesto-en" rows={4} value={form.manifesto_en} onChange={(e) => set("manifesto_en", e.target.value)} className={inputCls} />
        </Section>

        <Section title="Hero beeld (homepagina)">
          <div className="flex items-start gap-4">
            {form.hero_image_url && (
              <img src={resolveImage(form.hero_image_url)} alt="" className="w-48 h-28 object-cover bg-stoel-paperDeep border border-stoel-line" />
            )}
            <div className="flex flex-col gap-2 flex-1">
              <label className="inline-flex items-center gap-2 px-3 py-2 border border-stoel-ink text-sm cursor-pointer hover:bg-stoel-ink hover:text-stoel-paper">
                <Upload size={14} /> Upload
                <input data-testid="hero-upload" type="file" accept="image/*" hidden onChange={upload} />
              </label>
              <button data-testid="hero-regen" onClick={regen} disabled={imgBusy} className="inline-flex items-center gap-2 px-3 py-2 border border-stoel-line text-sm hover:bg-stoel-paperDeep disabled:opacity-50">
                <RefreshCw size={14} /> {imgBusy ? "Bezig…" : "Regenereer AI"}
              </button>
              {altUrl && (
                <button
                  data-testid="hero-swap-alt"
                  onClick={swapHero}
                  disabled={imgBusy}
                  className="inline-flex items-center gap-2 px-3 py-2 border border-stoel-terracotta text-stoel-terracotta text-sm hover:bg-stoel-terracotta hover:text-stoel-paper disabled:opacity-50"
                >
                  <Shuffle size={14} /> Wissel naar variant B
                </button>
              )}
            </div>
            {altUrl && (
              <div className="flex flex-col items-center gap-1">
                <img
                  src={resolveImage(altUrl)}
                  alt="Alternate"
                  className="w-24 h-14 object-cover bg-stoel-paperDeep border border-dashed border-stoel-walnut opacity-70"
                />
                <span className="text-[10px] uppercase tracking-[0.2em] text-stoel-walnut">Alt</span>
              </div>
            )}
          </div>
        </Section>

        <Section title="Over ons — NL">
          <textarea data-testid="settings-about-nl" rows={5} value={form.about_us_nl} onChange={(e) => set("about_us_nl", e.target.value)} className={inputCls} />
        </Section>

        <Section title="Over ons — EN">
          <textarea data-testid="settings-about-en" rows={5} value={form.about_us_en} onChange={(e) => set("about_us_en", e.target.value)} className={inputCls} />
        </Section>

        <Section title="Admin whitelist (alleen-lezen)">
          <ul className="space-y-1 text-sm font-mono">
            {whitelist.map((e) => <li key={e}>{e}</li>)}
          </ul>
          <p className="text-xs text-stoel-inkSoft mt-2">Beheer via server env-variabele ADMIN_EMAILS.</p>
        </Section>

        <ChangePasswordCard />

        {error && <div className="bg-stoel-terracotta/10 border-l-2 border-stoel-terracotta p-3 text-sm">{error}</div>}

        <div className="flex items-center gap-4 pt-4 border-t border-stoel-line">
          <button data-testid="settings-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-6 py-3 bg-stoel-ink text-stoel-paper text-sm hover:bg-stoel-terracotta disabled:opacity-50">
            <Save size={14} /> {saving ? "Opslaan…" : "Opslaan"}
          </button>
          {savedAt && <span className="text-xs text-stoel-walnut" data-testid="settings-saved-at">Opgeslagen om {savedAt.toLocaleTimeString()}</span>}
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-stoel-paper border border-stoel-line px-3 py-2 text-sm focus:outline-none focus:border-stoel-ink";
function Section({ title, children }) {
  return (
    <section className="bg-stoel-paper border border-stoel-line p-6">
      <div className="text-[10px] uppercase tracking-[0.2em] text-stoel-walnut mb-3">{title}</div>
      {children}
    </section>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setMsg("");
    if (next.length < 8) {
      setErr("Nieuw wachtwoord moet minimaal 8 tekens zijn.");
      return;
    }
    if (next !== confirm) {
      setErr("Bevestiging komt niet overeen.");
      return;
    }
    setBusy(true);
    try {
      await adminApi.post("/auth/change-password", { current_password: current, new_password: next });
      setMsg("Wachtwoord gewijzigd.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Wijzigen mislukt.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="change-password-card" className="bg-stoel-paper border border-stoel-line p-6">
      <div className="text-[10px] uppercase tracking-[0.2em] text-stoel-walnut mb-4 flex items-center gap-2">
        <Lock size={12} /> Wachtwoord wijzigen
      </div>
      <form onSubmit={submit} className="space-y-3 max-w-md">
        <input
          data-testid="cp-current"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          placeholder="Huidig wachtwoord"
          className={inputCls}
          required
        />
        <input
          data-testid="cp-new"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          placeholder="Nieuw wachtwoord (min. 8 tekens)"
          className={inputCls}
          required
        />
        <input
          data-testid="cp-confirm"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Bevestig nieuw wachtwoord"
          className={inputCls}
          required
        />
        {err && <div data-testid="cp-error" className="text-xs text-stoel-terracotta">{err}</div>}
        {msg && <div data-testid="cp-success" className="text-xs text-stoel-walnut">{msg}</div>}
        <button
          data-testid="cp-submit"
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 px-5 py-2 bg-stoel-ink text-stoel-paper text-sm hover:bg-stoel-terracotta disabled:opacity-50"
        >
          {busy ? "Bezig…" : "Wachtwoord wijzigen"}
        </button>
      </form>
    </section>
  );
}
